
import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router';

import { createApiClient, useApiClient } from "../../components/useApiClient";
import { useUser } from '../../components/UserContext';
import { useCurrency } from '../../components/CurrencyContext';
import { useFilter } from '../../components/Filter/ArtworkFilterContext';

import "./DashboardStyleSheet.css";

import {
    DashboardFilterState,
    PortfolioSummaryResponse,
    KpiDto,
    TimeSeriesPoint,
    CompositionSlice,
    TopItem,
    AlertItem,
    ActivityEvent,
    PortfolioGrowthPoint
} from "../../models/Dashboard";

import { PortfolioDto } from "../../models/Portfolio";

// Form Compomnents
import DashboardControls from "./DashboardControls";
import AlertsBanner from "./AlertsBanner";
import KpiStrip from "./KpiStrip";
import ValueTrendCard from "./ValueTrendCard";
import TopArtworksCard from "./TopArtworksCard";
import PortfolioGrowthCard from "./PortfolioGrowthCard";
import ValuationGapsCard from "./ValuationGapsCard";
import AcquisitionVsCurrentCard from "./AcquisitionVsCurrentCard";
import ValuationHealthCard from "./ValuationHealthCard";
import CompositionCard from "./CompositionCard";
import { DashboardFilters } from "./DashboardFilters";

const DashboardPage: React.FC = () => {

    const [query, setQuery] = useState<DashboardFilterState>({
        portfolioId: "all",
        valuationType: "market value",
        currencyCode: "GBP",
        asOfDate: new Date().toISOString().split("T")[0],
    });

    const { get, put, post, delete: del } = useApiClient();
    const { activeClientId, getAccessToken } = useUser();

    const { currency } = useCurrency();
    const { filterParams, setFilterParams } = useFilter();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [data, setData] = useState<PortfolioSummaryResponse | null>(null);
    const [portfolios, setPortfolios] = useState<PortfolioDto[]>([]);

    const [portfolioGrowth, setPortfolioGrowth] = useState<PortfolioGrowthPoint[]>([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {

                const accessToken = await getAccessToken();

                if (accessToken) {

                    const portfolios = await get<PortfolioDto[]>(`portfolios`, accessToken);
                    setPortfolios(portfolios);

                    const params = new URLSearchParams({
                        portfolioId: query.portfolioId,
                        valuationType: query.valuationType,
                        currency: currency,
                        asOfDate: query.asOfDate,
                    });

                    const summary = await get<PortfolioSummaryResponse>(`/dashboard/portfolioSummary?${params.toString()}`, accessToken);

                    if (cancelled) return;

                    setData(summary);

                    //setKpis(summary.kpis);
                    //setValueTrend(summary.valueTrend);
                    //setArtistComposition(summary.artistComposition);
                    //setCategoryComposition(summary.categoryComposition);
                    //setLocationComposition(summary.locationComposition);
                    //setTopArtworks(summary.topArtworks);
                    //setTopArtists(summary.topArtists);
                    //setAlerts(summary.alerts);
                    //setActivity(summary.activity);

                    // after fetching JSON
                    //const mappedGrowth: PortfolioGrowthPoint[] = summary.portfolioGrowth?.map((p) => ({
                    //    date: new Date(p.date).toISOString().substring(0, 10),
                    //    acquisitionValue: p.acquisitionValue,
                    //    currentMarketValue: p.currentMarketValue,
                    //    currentInsuranceValue: p.currentInsuranceValue,
                    //    itemsAdded: p.itemsAdded,
                    //    incrementalValue: p.currentMarketValue - p.acquisitionValue,
                    //})) ?? [];

                    //setPortfolioGrowth(mappedGrowth);
                }

            } catch (err: any) {
                console.info("Get portfolio error", err);

                if (!cancelled) {
                    setError(err.message ?? "Failed to load portfolio summary");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [query, currency]);

    useEffect(() => {

        setQuery({
            portfolioId: "all",
            valuationType: "market value",
            currencyCode: "GBP",
            asOfDate: new Date().toISOString().split("T")[0],
        });

    }, [activeClientId]);

    return (
        <div className="dashboard-page">
            <div className="dashboard-header">
                <h1>Portfolio Overview</h1>
                <DashboardFilters value={query} onChange={setQuery} />
            </div>
            {/*<DashboardControls query={query} onChange={setQuery} portfolios={portfolios} />*/}

            {data?.alerts && <AlertsBanner alerts={data.alerts} />}

            <KpiStrip kpis={data?.kpis ?? []} />

            <ValuationHealthCard health={data?.valuationHealth} />

            <div className="dashboard-row">
                <ValueTrendCard data={data?.valueTrend ?? []} currency={currency} />
                <CompositionCard
                    artistData={data?.artistComposition ?? []}
                    categoryData={data?.categoryComposition ?? []}
                    locationData={data?.locationComposition ?? []}
                    loading={loading}
                />
            </div>

            <div className="dashboard-row">
                <TopArtworksCard pageTitle="Top Artworks by Value" items={data?.topArtworks ?? []} showImage={true} />
                <TopArtworksCard pageTitle="Top Artists by Value" items={data?.topArtists ?? []} showImage={false} />
            </div>

            {/*<PortfolioGrowthCard data={data?.portfolioGrowth ?? []} />*/}

            <div className="dashboard-row">
                <ValuationGapsCard gaps={data?.valuationGaps ?? []} />
                <AcquisitionVsCurrentCard items={data?.acquisitionVsCurrent ?? []} />
            </div>

            
        </div>
    );
};
export default DashboardPage;